// First-visit constellation-tesseract loader. Self-injects DOM + styles.
// One per browser session: sessionStorage flag set on dismiss.
// Ported verbatim from thoughtful-oasis-v8.2/loader.js (tesseract half only;
// the data-i18n half is handled by React via useLanguage).

export function initTesseractLoader(): void {
  if (typeof document === 'undefined') return;
  try {
    if (sessionStorage.getItem('to:loader:seen') === '1') return;
  } catch {
    /* private mode etc. — fall through */
  }

  const TEAL = '#2DA88D';
  const CREAM = '#F0EBE3';
  const INK = '#1A1F1B';
  const INK_SOFT = '#5A5F5A';

  const style = document.createElement('style');
  style.textContent =
    '.to-loader{position:fixed;inset:0;z-index:9999;background:' + CREAM + ';' +
    'display:flex;align-items:center;justify-content:center;flex-direction:column;gap:40px;' +
    'transition:opacity .55s cubic-bezier(.4,0,.2,1),visibility 0s linear .55s}' +
    '.to-loader.is-done{opacity:0;visibility:hidden}' +
    '.to-loader__viz{width:clamp(220px,28vw,340px);height:clamp(220px,28vw,340px);' +
    'display:flex;align-items:center;justify-content:center;color:' + TEAL + '}' +
    '.to-loader__viz svg{display:block;overflow:visible;width:100%;height:100%}' +
    '.to-loader__caption{display:flex;flex-direction:column;align-items:center;gap:18px}' +
    '.to-loader__mark{font-family:Urbanist,Inter,system-ui,sans-serif;font-weight:500;' +
    'font-size:1.0625rem;color:' + INK + ';letter-spacing:-0.01em;margin:0}' +
    '.to-loader__rule{width:36px;height:1px;background:' + TEAL + '}' +
    '.to-loader__status{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:.75rem;' +
    'font-weight:500;text-transform:uppercase;letter-spacing:.18em;color:' + INK_SOFT + ';margin:0}' +
    '.to-loader__status::after{content:"";display:inline-block;width:1.6em;text-align:left;' +
    'animation:to-loader-dots 1.2s steps(4,end) infinite}' +
    '@keyframes to-loader-dots{0%{content:""}25%{content:"."}50%{content:".."}75%{content:"..."}100%{content:""}}' +
    '@media (prefers-reduced-motion:reduce){' +
    '.to-loader{transition:none}.to-loader__status::after{animation:none;content:""}}';
  (document.head || document.documentElement).appendChild(style);

  const loader = document.createElement('div');
  loader.className = 'to-loader';
  loader.setAttribute('role', 'status');
  loader.setAttribute('aria-label', 'Loading');
  loader.innerHTML =
    '<div class="to-loader__viz"></div>' +
    '<div class="to-loader__caption">' +
    '<p class="to-loader__mark">Thoughtful Oasis</p>' +
    '<span class="to-loader__rule"></span>' +
    '<p class="to-loader__status">Loading</p>' +
    '</div>';
  document.documentElement.appendChild(loader);

  const V4: number[][] = [];
  for (let i = 0; i < 16; i++) {
    V4.push([
      i & 1 ? 1 : -1,
      i & 2 ? 1 : -1,
      i & 4 ? 1 : -1,
      i & 8 ? 1 : -1,
    ]);
  }
  function rotXW(v: number[], a: number): number[] {
    const c = Math.cos(a),
      s = Math.sin(a);
    return [c * v[0] - s * v[3], v[1], v[2], s * v[0] + c * v[3]];
  }
  function rotYW(v: number[], a: number): number[] {
    const c = Math.cos(a),
      s = Math.sin(a);
    return [v[0], c * v[1] - s * v[3], v[2], s * v[1] + c * v[3]];
  }
  function rotYZ(v: number[], a: number): number[] {
    const c = Math.cos(a),
      s = Math.sin(a);
    return [v[0], c * v[1] - s * v[2], s * v[1] + c * v[2], v[3]];
  }
  function project(v: number[]) {
    const k4 = 1 / (2.5 - v[3]);
    const x3 = v[0] * k4,
      y3 = v[1] * k4,
      z3 = v[2] * k4;
    const k3 = 1 / (3 - z3);
    return { x: x3 * k3, y: y3 * k3, w: v[3] };
  }
  function tessVerts(t: number, scale: number) {
    const out: { x: number; y: number; w: number }[] = [];
    for (let i = 0; i < 16; i++) {
      let p = rotXW(V4[i], t * 0.42);
      p = rotYW(p, t * 0.31);
      p = rotYZ(p, t * 0.2);
      const pr = project(p);
      out.push({ x: pr.x * scale, y: pr.y * scale, w: pr.w });
    }
    return out;
  }

  const viz = loader.querySelector('.to-loader__viz')!;
  const size = 320;
  const scale = size * 0.34;
  const weight = 3.4;
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('viewBox', `${-size / 2} ${-size / 2} ${size} ${size}`);
  viz.appendChild(svg);
  const circles: SVGCircleElement[] = [];
  for (let j = 0; j < 16; j++) {
    const c = document.createElementNS(svgNS, 'circle');
    c.setAttribute('fill', TEAL);
    svg.appendChild(c);
    circles.push(c);
  }

  const reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const speed = reduced ? 0 : 1.0;
  let start: number | null = null;
  let raf = 0;

  function frame(ts: number) {
    if (start == null) start = ts;
    const t = ((ts - start) / 1000) * speed;
    const v = tessVerts(t, scale);
    for (let i = 0; i < 16; i++) {
      const d = Math.max(0, Math.min(1, (v[i].w + 1.4) / 2.8));
      const r = weight * (0.7 + d * 1.8);
      const op = 0.28 + d * 0.68;
      const ci = circles[i];
      ci.setAttribute('cx', String(v[i].x));
      ci.setAttribute('cy', String(v[i].y));
      ci.setAttribute('r', String(r));
      ci.setAttribute('opacity', String(op));
    }
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  const minVisible = 700;
  const startedAt = performance.now();

  function dismiss() {
    const elapsed = performance.now() - startedAt;
    const wait = Math.max(0, minVisible - elapsed);
    setTimeout(() => {
      loader.classList.add('is-done');
      try {
        sessionStorage.setItem('to:loader:seen', '1');
      } catch {
        /* noop */
      }
      setTimeout(() => {
        cancelAnimationFrame(raf);
        if (loader.parentNode) loader.parentNode.removeChild(loader);
      }, 650);
    }, wait);
  }

  if (document.readyState === 'complete') {
    dismiss();
  } else {
    window.addEventListener('load', dismiss, { once: true });
  }
}
